// The Security Audit page's one-line summary of a row (kept out of the page so it can be tested alone).
import { format } from 'date-fns';
import { DEFAULT_OWNER_TITLE } from '../../hooks/useOwnerTitle';

/**
 * A row's action as the office reads it: the server's label (`GET /security-audit/actions`, the
 * same table as the Action Type filter), never the raw enum (#616: `LOGIN_LINK_SENT` is labelled
 * "Login Link Issued" because the row is written before the email goes). Until the labels load,
 * the spaced-out enum stands in.
 */
export function actionLabel(action: string, labels: ReadonlyMap<string, string>): string {
  return labels.get(action) ?? action.replace(/_/g, ' ');
}

/** When a row happened, on one line: `26/10/2026 11:30 am` (dd/mm/yyyy, Critical Rule 7). */
export function formatAuditTime(createdAt: string): string {
  return format(new Date(createdAt), 'dd/MM/yyyy h:mm aaa');
}

/** The one-line summary of a row's details; the full JSON is a click away. */
export function auditDetailsSummary(details: Record<string, unknown>): string {
  const summary: string[] = [];
  if (details.previousRole && details.newRole) {
    summary.push(`${details.previousRole} → ${details.newRole}`);
  }
  if (details.previousEmail && details.newEmail) {
    summary.push(`${details.previousEmail} → ${details.newEmail}`);
  }
  if (details.targetEmail) {
    summary.push(details.targetEmail as string);
  }
  if (typeof details.parentLogins === 'number' && typeof details.teacherLogins === 'number') {
    summary.push(`${details.parentLogins} parent, ${details.teacherLogins} teacher logins`);
  }
  if ('ownerTitle' in details) {
    // `null` is the default title (roles slice 2: the owner-title save).
    const title = (v: unknown) => (typeof v === 'string' && v ? v : DEFAULT_OWNER_TITLE);
    summary.push(`Title: ${title(details.previousOwnerTitle)} → ${title(details.ownerTitle)}`);
  }
  if (typeof details.ownerTitleInMessages === 'boolean') {
    summary.push(`Title in Messages: ${details.ownerTitleInMessages ? 'on' : 'off'}`);
  }
  return summary.join(' | ');
}
